// btc15/glossaryArt.js — a small diagram for every glossary entry.
//
// The user's ask, 2026-09-16: "pictures for every segment". These are drawn
// rather than fetched: inline SVG, no assets, no network, themed from the
// same palette the sheet uses, and legible at phone width.
//
// Where a diagram can show the LIVE window it does — the fee curve carries a
// marker at the price on the book right now, the normal curve carries this
// window's z. A picture of the thing in front of you teaches more than a
// picture of a hypothetical one.
//
// Every diagram is a pure function of (palette, ctx). No state, no effects,
// no randomness: the same inputs always draw the same picture, which is what
// makes them testable.

import { knownSoFarAt } from "./study";

const W = 240, H = 90;            // one viewBox for all of them, so they stack evenly
const BASE = 72;                  // the x-axis
const LEFT = 16, RIGHT = 228;

const num = (v) => (v == null || Number.isNaN(Number(v)) ? null : Number(v));
const sx = (t) => LEFT + t * (RIGHT - LEFT);                       // 0..1 → x
const sy = (v, max = 1) => BASE - Math.max(0, Math.min(1, v / max)) * (BASE - 10);

/** A polyline through fn(t) for t in 0..1.
 *  Non-finite points are DROPPED rather than emitted: a diagram that wants a
 *  gap (the volatility rule draws only the fast half of its tape) would
 *  otherwise put "NaN,NaN" into the points attribute, which is invalid SVG
 *  and renders as nothing at all. */
function curve(fn, max = 1, steps = 48) {
  const pts = [];
  for (let i = 0; i <= steps; i += 1) {
    const t = i / steps;
    const v = fn(t);
    if (!Number.isFinite(v)) continue;
    const y = sy(v, max);
    if (!Number.isFinite(y)) continue;
    pts.push(`${sx(t).toFixed(1)},${y.toFixed(1)}`);
  }
  return pts.join(" ");
}

const normal = (z) => Math.exp(-0.5 * z * z);

// ── shared furniture ──────────────────────────────────────────────────────
const Axis = ({ P, label, left, right }) => (
  <>
    <line x1={LEFT} y1={BASE} x2={RIGHT} y2={BASE} stroke={P.border} strokeWidth="1" />
    {left && <text x={LEFT} y={BASE + 12} fill={P.faint} fontSize="8">{left}</text>}
    {right && <text x={RIGHT} y={BASE + 12} fill={P.faint} fontSize="8" textAnchor="end">{right}</text>}
    {label && <text x={(LEFT + RIGHT) / 2} y={BASE + 12} fill={P.faint} fontSize="8" textAnchor="middle">{label}</text>}
  </>
);

const Marker = ({ P, t, text, color }) => (
  <>
    <line x1={sx(t)} y1={10} x2={sx(t)} y2={BASE} stroke={color} strokeWidth="1.5" strokeDasharray="3 2" />
    <text x={sx(t)} y={8} fill={color} fontSize="8.5" fontWeight="700" textAnchor={t > 0.8 ? "end" : t < 0.2 ? "start" : "middle"}>{text}</text>
  </>
);

const Bar = ({ P, x, w, top, color, label, value }) => (
  <>
    <rect x={x} y={top} width={w} height={Math.max(0, BASE - top)} fill={color} opacity="0.85" rx="2" />
    {value && <text x={x + w / 2} y={top - 3} fill={color} fontSize="8.5" fontWeight="800" textAnchor="middle">{value}</text>}
    {label && <text x={x + w / 2} y={BASE + 12} fill={P.faint} fontSize="8" textAnchor="middle">{label}</text>}
  </>
);

// ── the diagrams ──────────────────────────────────────────────────────────
// Each is (P, ctx) => svg children. Keyed by glossary id.
const ART = {
  // ---- the window ----
  target: (P) => (
    <>
      <rect x={LEFT} y={30} width={40} height={20} fill={P.accent} opacity="0.35" rx="2" />
      <text x={LEFT + 20} y={26} fill={P.accent} fontSize="7.5" textAnchor="middle">60s avg</text>
      <line x1={LEFT + 40} y1={40} x2={LEFT + 40} y2={62} stroke={P.text} strokeWidth="1.5" />
      <text x={LEFT + 44} y={60} fill={P.text} fontSize="8" fontWeight="700">= target</text>
      <rect x={LEFT + 40} y={30} width={130} height={20} fill={P.border} opacity="0.4" rx="2" />
      <text x={LEFT + 105} y={44} fill={P.muted} fontSize="8" textAnchor="middle">the 15-minute window</text>
      <rect x={LEFT + 170} y={30} width={40} height={20} fill={P.green} opacity="0.35" rx="2" />
      <text x={LEFT + 190} y={26} fill={P.green} fontSize="7.5" textAnchor="middle">60s avg</text>
      <Axis P={P} left="open" right="bell" />
    </>
  ),
  now: (P) => (
    <>
      <polyline points={curve((t) => 0.45 + 0.18 * Math.sin(t * 11) + 0.1 * t)} fill="none" stroke={P.green} strokeWidth="1.5" />
      <circle cx={sx(1)} cy={sy(0.45 + 0.18 * Math.sin(11) + 0.1)} r="3.5" fill={P.green} />
      <Axis P={P} label="the Coinbase tape, many prints a second" />
    </>
  ),
  gap: (P, x) => {
    const above = num(x?.btc) != null && num(x?.target) != null ? x.btc >= x.target : true;
    const col = above ? P.green : P.red;
    return (
      <>
        <line x1={LEFT} y1={46} x2={RIGHT} y2={46} stroke={P.muted} strokeDasharray="4 3" strokeWidth="1.2" />
        <text x={RIGHT} y={43} fill={P.muted} fontSize="8" textAnchor="end">target</text>
        <polyline points={curve((t) => 0.42 + 0.16 * Math.sin(t * 7) + (above ? 0.16 : -0.14) * t)} fill="none" stroke={col} strokeWidth="1.8" />
        <line x1={sx(0.88)} y1={46} x2={sx(0.88)} y2={sy(0.42 + 0.16 * Math.sin(6.2) + (above ? 0.16 : -0.14) * 0.88)} stroke={col} strokeWidth="1.5" />
        <text x={sx(0.86)} y={above ? 24 : 66} fill={col} fontSize="8.5" fontWeight="800" textAnchor="end">the gap</text>
        <Axis P={P} label="dollars above or below — z turns this into odds" />
      </>
    );
  },
  tps: (P) => (
    <>
      {[0.55, 0.8, 0.4, 0.95, 0.7, 0.85, 0.5, 0.75].map((v, i) => (
        <Bar key={i} P={P} x={LEFT + 6 + i * 26} w={16} top={sy(v)} color={P.green} />
      ))}
      <Axis P={P} label="prints arriving, one bar a second" />
    </>
  ),
  knownsofar: (P, x) => {
    const m = Math.max(0, Math.min(15, num(x?.minute) ?? 10)) / 15;
    const f = (t) => knownSoFarAt(t * 15);                          // the study's table, not a made-up curve
    return (
      <>
        <polyline points={curve(f)} fill="none" stroke={P.accent} strokeWidth="2" />
        <Marker P={P} t={m} text={`m${Math.round(m * 15)}`} color={P.text} />
        <circle cx={sx(m)} cy={sy(f(m))} r="3" fill={P.accent} />
        <text x={LEFT} y={sy(0.5) - 3} fill={P.faint} fontSize="8">50%</text>
        <text x={RIGHT} y={18} fill={P.faint} fontSize="8" textAnchor="end">91% by m13</text>
        <Axis P={P} left="m0" right="m15" />
      </>
    );
  },
  settlement: (P) => (
    <>
      <rect x={LEFT + 150} y={28} width={44} height={22} fill={P.accent} opacity="0.3" rx="2" />
      <text x={LEFT + 172} y={24} fill={P.accent} fontSize="7.5" textAnchor="middle">last 60s avg</text>
      <line x1={LEFT} y1={52} x2={RIGHT} y2={52} stroke={P.muted} strokeDasharray="4 3" />
      <text x={LEFT + 2} y={49} fill={P.muted} fontSize="7.5">target</text>
      <Bar P={P} x={LEFT + 10} w={34} top={sy(0.95)} color={P.green} label="if ≥" value="$1" />
      <Bar P={P} x={LEFT + 60} w={34} top={BASE - 2} color={P.red} label="if <" value="$0" />
      <Axis P={P} />
    </>
  ),

  // ---- the market ----
  price: (P, x) => {
    const ask = num(x?.yesAsk) ?? 0.75, bid = num(x?.yesBid) ?? 0.73;
    return (
      <>
        <rect x={LEFT} y={34} width={RIGHT - LEFT} height={14} fill={P.border} opacity="0.5" rx="7" />
        <rect x={LEFT} y={34} width={(RIGHT - LEFT) * ask} height={14} fill={P.green} opacity="0.5" rx="7" />
        <Marker P={P} t={bid} text={`bid ${Math.round(bid * 100)}¢`} color={P.muted} />
        <Marker P={P} t={ask} text={`ask ${Math.round(ask * 100)}¢`} color={P.green} />
        <Axis P={P} left="0¢ · never" right="100¢ · certain" />
      </>
    );
  },
  mid: (P, x) => {
    const ask = num(x?.yesAsk) ?? 0.75, bid = num(x?.yesBid) ?? 0.73, mid = (ask + bid) / 2;
    return (
      <>
        <line x1={sx(bid)} y1={44} x2={sx(ask)} y2={44} stroke={P.accent} strokeWidth="6" opacity="0.4" />
        <Marker P={P} t={bid} text="bid" color={P.muted} />
        <Marker P={P} t={ask} text="ask" color={P.muted} />
        <circle cx={sx(mid)} cy={44} r="4.5" fill={P.accent} />
        <text x={sx(mid)} y={62} fill={P.accent} fontSize="8.5" fontWeight="800" textAnchor="middle">mid</text>
        <Axis P={P} label="the market's fairest single number" />
      </>
    );
  },
  spread: (P, x) => {
    const ask = num(x?.yesAsk) ?? 0.75, bid = num(x?.yesBid) ?? 0.73;
    return (
      <>
        <rect x={sx(bid)} y={30} width={Math.max(3, sx(ask) - sx(bid))} height={26} fill={P.red} opacity="0.4" rx="2" />
        <text x={(sx(bid) + sx(ask)) / 2} y={24} fill={P.red} fontSize="8.5" fontWeight="800" textAnchor="middle">you pay this</text>
        <Marker P={P} t={bid} text="sell here" color={P.muted} />
        <Marker P={P} t={ask} text="buy here" color={P.muted} />
        <Axis P={P} label="paid the instant you enter" />
      </>
    );
  },
  fee: (P, x) => {
    const ask = num(x?.yesAsk);
    const floor = 0.70;
    return (
      <>
        {/* share of stake: 0.07(1-p), the curve that actually matters */}
        <polyline points={curve((t) => 0.07 * (1 - t), 0.07)} fill="none" stroke={P.red} strokeWidth="2" />
        <text x={LEFT + 3} y={18} fill={P.red} fontSize="8" fontWeight="700">fee as a share of your stake</text>
        {/* the fee per contract: 0.07 p(1-p), the hump */}
        <polyline points={curve((t) => 0.07 * t * (1 - t), 0.07)} fill="none" stroke={P.muted} strokeWidth="1.2" strokeDasharray="3 2" />
        <text x={sx(0.5)} y={sy(0.0175, 0.07) - 4} fill={P.muted} fontSize="7.5" textAnchor="middle">per contract</text>
        <Marker P={P} t={floor} text="70¢ floor" color={P.green} />
        {ask != null && <circle cx={sx(ask)} cy={sy(0.07 * (1 - ask), 0.07)} r="3.5" fill={P.red} />}
        <text x={LEFT} y={BASE + 12} fill={P.faint} fontSize="8">25¢ · 5.25%</text>
        <text x={RIGHT} y={BASE + 12} fill={P.faint} fontSize="8" textAnchor="end">90¢ · 0.70%</text>
        <line x1={LEFT} y1={BASE} x2={RIGHT} y2={BASE} stroke={P.border} />
      </>
    );
  },
  allin: (P, x) => {
    const ask = num(x?.yesAsk) ?? 0.75;
    const f = 0.07 * ask * (1 - ask);
    const total = ask + f;
    return (
      <>
        <rect x={LEFT} y={34} width={(RIGHT - LEFT) * (ask / total)} height={20} fill={P.accent} opacity="0.7" rx="2" />
        <rect x={LEFT + (RIGHT - LEFT) * (ask / total)} y={34} width={Math.max(4, (RIGHT - LEFT) * (f / total))} height={20} fill={P.red} opacity="0.85" rx="2" />
        <text x={LEFT + 6} y={48} fill={P.text} fontSize="9" fontWeight="700">price</text>
        <text x={RIGHT - 2} y={28} fill={P.red} fontSize="8" fontWeight="800" textAnchor="end">+ fee</text>
        <Axis P={P} label="what one contract really costs" />
      </>
    );
  },
  multiple: (P, x) => {
    const ask = num(x?.yesAsk) ?? 0.75;
    const total = ask + 0.07 * ask * (1 - ask);
    return (
      <>
        <Bar P={P} x={LEFT + 20} w={50} top={sy(total)} color={P.red} label="you pay" value={`${Math.round(total * 100)}¢`} />
        <Bar P={P} x={LEFT + 110} w={50} top={sy(1)} color={P.green} label="if right" value="$1" />
        <Bar P={P} x={LEFT + 110} w={50} top={BASE - 2} color={P.red} label="" value="" />
        <text x={RIGHT - 4} y={30} fill={P.faint} fontSize="8" textAnchor="end">if wrong: $0</text>
        <Axis P={P} />
      </>
    );
  },
  contracts: (P, x) => {
    const ask = num(x?.yesAsk) ?? 0.75;
    const n = Math.min(12, Math.round((num(x?.stake) ?? 5) / ask));
    return (
      <>
        {[...Array(n)].map((_, i) => (
          <rect key={i} x={LEFT + (i % 6) * 34} y={i < 6 ? 26 : 48} width={28} height={16} fill={P.accent} opacity="0.7" rx="2" />
        ))}
        <text x={LEFT} y={20} fill={P.muted} fontSize="8">{`stake ÷ price = ${n} contracts, each paying $1`}</text>
        <Axis P={P} />
      </>
    );
  },
  payout: (P, x) => {
    const ask = num(x?.yesAsk) ?? 0.75;
    const s = num(x?.stake) ?? 5;
    const n = s / ask;
    const f = 0.07 * ask * (1 - ask) * n;
    const max = Math.max(n, s + f);
    return (
      <>
        <Bar P={P} x={LEFT + 16} w={46} top={sy((s + f) / max)} color={P.red} label="out" value={`${(s + f).toFixed(2)}`} />
        <Bar P={P} x={LEFT + 84} w={46} top={sy(n / max)} color={P.green} label="back" value={`${n.toFixed(2)}`} />
        <Bar P={P} x={LEFT + 152} w={46} top={sy(Math.max(0, n - s - f) / max)} color={P.accent} label="net" value={`${(n - s - f).toFixed(2)}`} />
        <Axis P={P} />
      </>
    );
  },
  // An SVG <text> whose children are an expression NEXT TO literal text
  // gets two DOM text nodes, and only the last one is laid out: the book's
  // prices rendered as a bare "¢" (2026-09-16). One string, one node.
  book: (P) => (
    <>
      {[[0.58, 30, P.red], [0.57, 22, P.red]].map(([px, w, col], i) => (
        <g key={i}><rect x={sx(0.52)} y={18 + i * 12} width={w} height={9} fill={col} opacity="0.6" />
          <text x={sx(0.5)} y={26 + i * 12} fill={P.faint} fontSize="7.5" textAnchor="end">{`${Math.round(px * 100)}¢`}</text></g>
      ))}
      {[[0.56, 34, P.green], [0.55, 24, P.green]].map(([px, w, col], i) => (
        <g key={i}><rect x={sx(0.52)} y={46 + i * 12} width={w} height={9} fill={col} opacity="0.6" />
          <text x={sx(0.5)} y={54 + i * 12} fill={P.faint} fontSize="7.5" textAnchor="end">{`${Math.round(px * 100)}¢`}</text></g>
      ))}
      {/* the widest bar ends at sx(0.52) + 46, and the frame at 240: these
          have to say it in what is left, or the browser clips them away */}
      <text x={sx(0.52) + 50} y={25} fill={P.red} fontSize="6.5">asks — sellers</text>
      <text x={sx(0.52) + 50} y={53} fill={P.green} fontSize="6.5">bids — buyers</text>
      <Axis P={P} label="the size behind a price" />
    </>
  ),

  // ---- the agents ----
  raw: (P) => (
    <>
      <line x1={LEFT} y1={46} x2={RIGHT} y2={46} stroke={P.border} />
      {[["atlas", 0.5, P.accent], ["comet", 0.66, P.green], ["drift", 0.34, P.red], ["ledger", 0.58, P.gold || P.accent], ["kestrel", 0.46, P.muted], ["echo", 0.54, P.text]].map(([k, t, col], i) => (
        <g key={k}>
          <circle cx={sx(t)} cy={46} r="3.5" fill={col} />
          <text x={sx(t)} y={i % 2 ? 62 : 36} fill={col} fontSize="7.5" textAnchor="middle">{k}</text>
        </g>
      ))}
      <text x={sx(0.5)} y={20} fill={P.faint} fontSize="8" textAnchor="middle">one fair value, six different tilts</text>
      <Axis P={P} left="Down" right="Up" />
    </>
  ),
  calibrated: (P, x) => {
    const m = num(x?.marketP) ?? 0.74;
    const r = num(x?.raw?.[x?.agentKey]) ?? 0.90;
    const cal = 0.25 * r + 0.75 * m;
    return (
      <>
        <line x1={LEFT} y1={46} x2={RIGHT} y2={46} stroke={P.border} />
        <line x1={sx(r)} y1={46} x2={sx(cal)} y2={46} stroke={P.accent} strokeWidth="5" opacity="0.4" />
        <circle cx={sx(r)} cy={46} r="4" fill={P.muted} />
        <text x={sx(r)} y={34} fill={P.muted} fontSize="8" textAnchor="middle">raw</text>
        <circle cx={sx(m)} cy={46} r="4" fill={P.text} />
        <text x={sx(m)} y={66} fill={P.text} fontSize="8" textAnchor="middle">market</text>
        <circle cx={sx(cal)} cy={46} r="5" fill={P.accent} />
        <text x={sx(cal)} y={30} fill={P.accent} fontSize="8.5" fontWeight="800" textAnchor="middle">trades here</text>
        <text x={sx(0.5)} y={18} fill={P.faint} fontSize="7.5" textAnchor="middle">75% of the way to the market</text>
        <Axis P={P} />
      </>
    );
  },
  fairvalue: (P) => (
    <>
      <polyline points={curve((t) => normal((t - 0.5) * 6))} fill="none" stroke={P.accent} strokeWidth="1.8" />
      <polygon points={`${curve((t) => (t > 0.5 ? normal((t - 0.5) * 6) : 0))} ${RIGHT},${BASE} ${sx(0.5)},${BASE}`} fill={P.green} opacity="0.3" />
      <line x1={sx(0.5)} y1={14} x2={sx(0.5)} y2={BASE} stroke={P.text} strokeWidth="1.2" strokeDasharray="3 2" />
      <text x={sx(0.5) + 3} y={20} fill={P.text} fontSize="8">target</text>
      <text x={sx(0.74)} y={44} fill={P.green} fontSize="9" fontWeight="800" textAnchor="middle">this share wins</text>
      <Axis P={P} label="where a wandering price could finish" />
    </>
  ),
  z: (P, x) => {
    let z = 0.6;
    const btc = num(x?.btc), tgt = num(x?.target), s = num(x?.sigma15);
    if (btc && tgt && s) {
      const m = Math.max(0, Math.min(15, num(x?.minute) ?? 0));
      const frac = m <= 14 ? ((14 - m) + 1 / 3) / 15 : Math.max(Math.pow(15 - m, 3) / 3 / 15, 0.0005);
      z = Math.log(btc / tgt) / (s * Math.sqrt(frac));
    }
    const t = Math.max(0.04, Math.min(0.96, 0.5 + z / 6));
    return (
      <>
        <polyline points={curve((u) => normal((u - 0.5) * 6))} fill="none" stroke={P.muted} strokeWidth="1.5" />
        <Marker P={P} t={t} text={`z ${z.toFixed(2)}`} color={z >= 0 ? P.green : P.red} />
        <text x={LEFT} y={BASE + 12} fill={P.faint} fontSize="8">−3σ</text>
        <text x={sx(0.5)} y={BASE + 12} fill={P.faint} fontSize="8" textAnchor="middle">target</text>
        <text x={RIGHT} y={BASE + 12} fill={P.faint} fontSize="8" textAnchor="end">+3σ</text>
        <line x1={LEFT} y1={BASE} x2={RIGHT} y2={BASE} stroke={P.border} />
      </>
    );
  },
  sigma: (P) => (
    <>
      <polyline points={curve((t) => normal((t - 0.5) * 10))} fill="none" stroke={P.muted} strokeWidth="1.3" strokeDasharray="3 2" />
      <polyline points={curve((t) => normal((t - 0.5) * 5))} fill="none" stroke={P.accent} strokeWidth="1.8" />
      <text x={sx(0.5)} y={18} fill={P.accent} fontSize="8" textAnchor="middle">σ₁₅ = 0.00212 — the ruler</text>
      <text x={RIGHT} y={40} fill={P.muted} fontSize="7.5" textAnchor="end">a faster tape widens it</text>
      <Axis P={P} label="how far 15 minutes can carry the price" />
    </>
  ),

  // ---- the decision ----
  // The funnel needs a gutter for its own labels. They used to sit at
  // RIGHT + 6 and LEFT − 6 — outside the viewBox, so the browser clipped
  // every one of them to two letters ("re", "pr", "cc"…) and the diagram
  // explained nothing (2026-09-16, caught by the tab audit). Bars now stop
  // at BAR_R and the labels live inside the frame.
  decision: (P) => {
    const BAR_R = 168, TOP = 15, STEP = 7.5;
    return (
      <>
        <text x={LEFT} y={9} fill={P.muted} fontSize="6.5">every 20s · each agent × each rule</text>
        {[["read", 1], ["price", 0.92], ["correct", 0.84], ["open?", 0.72], ["trigger?", 0.58], ["agree?", 0.4], ["price sane?", 0.26], ["afford?", 0.17]].map(([lab, w], i) => (
          <g key={lab}>
            <rect x={LEFT + (BAR_R - LEFT) * (1 - w) / 2} y={TOP + i * STEP} width={(BAR_R - LEFT) * w} height={5.5}
              fill={i === 5 ? P.accent : P.border} opacity={i === 5 ? 0.95 : 0.75} rx="2" />
            <text x={BAR_R + 5} y={TOP + i * STEP + 4.6} fill={i === 5 ? P.accent : P.faint} fontSize="6">{lab}</text>
          </g>
        ))}
        <text x={LEFT} y={84} fill={P.green} fontSize="6.5">a few of them become a wager</text>
      </>
    );
  },
  agrees: (P) => (
    <>
      <text x={LEFT} y={18} fill={P.faint} fontSize="8">the same tape, six different answers</text>
      {[["atlas", 0.8, true], ["comet", 0.88, true], ["drift", 0.42, false], ["ledger", 0.7, false], ["kestrel", 0.79, true], ["echo", 0.6, false]].map(([k, v, inn], i) => (
        <g key={k}>
          <rect x={LEFT + i * 36} y={sy(v)} width={26} height={BASE - sy(v)} fill={inn ? P.green : P.border} opacity={inn ? 0.85 : 0.6} rx="2" />
          <text x={LEFT + i * 36 + 13} y={BASE + 11} fill={inn ? P.green : P.faint} fontSize="7" textAnchor="middle">{inn ? "in" : "out"}</text>
        </g>
      ))}
      <line x1={LEFT} y1={sy(0.75)} x2={RIGHT} y2={sy(0.75)} stroke={P.red} strokeDasharray="3 2" strokeWidth="1.3" />
      <text x={RIGHT} y={sy(0.75) - 3} fill={P.red} fontSize="7.5" textAnchor="end">what the market charges</text>
      <line x1={LEFT} y1={BASE} x2={RIGHT} y2={BASE} stroke={P.border} />
    </>
  ),
  floor: (P) => (
    <>
      <polyline points={curve((t) => 0.07 * (1 - t), 0.07)} fill="none" stroke={P.red} strokeWidth="2" />
      <rect x={LEFT} y={10} width={sx(0.7) - LEFT} height={BASE - 10} fill={P.red} opacity="0.12" />
      <text x={(LEFT + sx(0.7)) / 2} y={30} fill={P.red} fontSize="8.5" fontWeight="800" textAnchor="middle">refused</text>
      <text x={(LEFT + sx(0.7)) / 2} y={42} fill={P.red} fontSize="7.5" textAnchor="middle">fee too big a share</text>
      <Marker P={P} t={0.7} text="70¢" color={P.green} />
      <text x={sx(0.86)} y={34} fill={P.green} fontSize="8" textAnchor="middle">tradable</text>
      <Axis P={P} left="25¢" right="100¢" />
    </>
  ),
  minedge: (P) => (
    <>
      <Bar P={P} x={LEFT + 20} w={54} top={sy(0.75)} color={P.muted} label="the ask" value="75¢" />
      <rect x={LEFT + 90} y={sy(0.75)} width={54} height={BASE - sy(0.75)} fill={P.muted} opacity="0.5" rx="2" />
      <rect x={LEFT + 90} y={sy(0.77)} width={54} height={sy(0.75) - sy(0.77)} fill={P.red} opacity="0.85" />
      <rect x={LEFT + 90} y={sy(0.82)} width={54} height={sy(0.77) - sy(0.82)} fill={P.accent} opacity="0.85" />
      <text x={LEFT + 117} y={sy(0.82) - 3} fill={P.accent} fontSize="8" fontWeight="800" textAnchor="middle">+ fee + 5¢</text>
      <text x={LEFT + 117} y={BASE + 11} fill={P.faint} fontSize="8" textAnchor="middle">must beat this</text>
      <line x1={LEFT} y1={BASE} x2={RIGHT} y2={BASE} stroke={P.border} />
    </>
  ),
  shots: (P) => (
    <>
      <Axis P={P} left="m0" right="m15" />
      {[[0.10, 0.23], [0.34, 0.46], [0.57, 0.68], [0.79, 0.90]].map(([a, b], i) => (
        <g key={i}>
          <rect x={sx(a)} y={36} width={sx(b) - sx(a)} height={16} fill={P.green} opacity="0.7" rx="2" />
          <text x={(sx(a) + sx(b)) / 2} y={48} fill={P.text} fontSize="7" textAnchor="middle">{`shot ${i + 1}`}</text>
          {i < 3 && <rect x={sx(b)} y={41} width={sx(0.06) - LEFT} height={6} fill={P.red} opacity="0.5" />}
        </g>
      ))}
      <text x={sx(0.5)} y={24} fill={P.faint} fontSize="7.5" textAnchor="middle">no cap — a minute of cooldown between</text>
    </>
  ),
  bankfloor: (P) => (
    <>
      <rect x={LEFT} y={26} width={RIGHT - LEFT} height={26} fill={P.border} opacity="0.5" rx="3" />
      <rect x={LEFT} y={26} width={(RIGHT - LEFT) * 0.62} height={26} fill={P.green} opacity="0.7" rx="3" />
      <Marker P={P} t={0.2} text="20% floor" color={P.red} />
      <text x={LEFT + 6} y={43} fill={P.text} fontSize="8.5" fontWeight="700">bank</text>
      <Axis P={P} label="below the floor it stops entering" />
    </>
  ),
  retired: (P) => (
    <>
      <rect x={LEFT} y={20} width={96} height={36} fill={P.accent} opacity="0.2" rx="4" stroke={P.accent} />
      <text x={LEFT + 48} y={36} fill={P.accent} fontSize="8.5" fontWeight="800" textAnchor="middle">stopped</text>
      <text x={LEFT + 48} y={48} fill={P.muted} fontSize="7" textAnchor="middle">a new version reopens it</text>
      <rect x={LEFT + 110} y={20} width={96} height={36} fill={P.red} opacity="0.18" rx="4" stroke={P.red} />
      <text x={LEFT + 158} y={36} fill={P.red} fontSize="8.5" fontWeight="800" textAnchor="middle">retired</text>
      <text x={LEFT + 158} y={48} fill={P.muted} fontSize="7" textAnchor="middle">nothing reopens it</text>
      <Axis P={P} label="a record vs a refuted premise" />
    </>
  ),
  "echo-rule": (P) => (
    <>
      <polyline points={curve((t) => 0.5 + 0.42 * Math.pow(t, 1.6))} fill="none" stroke={P.muted} strokeWidth="1.6" />
      <Marker P={P} t={0.68} text="buys ≥85¢" color={P.green} />
      <circle cx={sx(1)} cy={sy(0.92)} r="3.5" fill={P.green} />
      <text x={RIGHT} y={sy(0.92) - 6} fill={P.green} fontSize="8" textAnchor="end">holds to the bell</text>
      <Axis P={P} left="m10" right="bell" />
    </>
  ),
  "kestrel-rule": (P) => (
    <>
      <polyline points={curve((t) => 0.45 + 0.06 * Math.sin(t * 20))} fill="none" stroke={P.muted} strokeWidth="1.3" />
      <polyline points={curve((t) => (t > 0.55 ? 0.45 + 0.3 * Math.sin(t * 26) : NaN))} fill="none" stroke={P.accent} strokeWidth="1.8" />
      <Marker P={P} t={0.55} text="1.5× normal" color={P.accent} />
      <text x={sx(0.78)} y={20} fill={P.accent} fontSize="8" textAnchor="middle">only acts here</text>
      <Axis P={P} label="quiet tape ignored, fast tape read" />
    </>
  ),
  edge: (P) => (
    <>
      <line x1={LEFT} y1={26} x2={RIGHT} y2={26} stroke={P.border} strokeWidth="1.2" />
      <text x={LEFT} y={20} fill={P.faint} fontSize="7.5">the market's score</text>
      {[4, 7, 8, 8, 9, 9, 9, 10, 12, 14, 22, 24].map((v, i) => (
        <rect key={i} x={LEFT + 2 + i * 17.5} y={26} width={13} height={v * 1.6} fill={P.red} opacity="0.8" rx="1" />
      ))}
      <text x={sx(0.5)} y={BASE + 12} fill={P.red} fontSize="8" textAnchor="middle" fontWeight="700">all twelve scored worse — none beat it</text>
    </>
  ),

  // ---- exits ----
  mark: (P) => (
    <>
      <polyline points={curve((t) => 0.45 + 0.3 * t)} fill="none" stroke={P.green} strokeWidth="1.8" />
      <circle cx={sx(0.75)} cy={sy(0.675)} r="4" fill={P.green} />
      <line x1={sx(0.75)} y1={sy(0.675)} x2={RIGHT} y2={sy(0.675)} stroke={P.green} strokeDasharray="3 2" />
      <text x={RIGHT} y={sy(0.675) - 4} fill={P.green} fontSize="8" textAnchor="end">sell here, into the bid</text>
      <Axis P={P} label="what it would fetch right now" />
    </>
  ),
  take: (P) => (
    <>
      <polyline points={curve((t) => 0.4 + 0.45 * Math.min(t, 0.62) / 0.62)} fill="none" stroke={P.green} strokeWidth="1.8" />
      <line x1={LEFT} y1={sy(0.85)} x2={RIGHT} y2={sy(0.85)} stroke={P.green} strokeDasharray="4 3" />
      <text x={RIGHT} y={sy(0.85) - 4} fill={P.green} fontSize="8" textAnchor="end">+20¢ → sell</text>
      <circle cx={sx(0.62)} cy={sy(0.85)} r="4" fill={P.green} />
      <Axis P={P} />
    </>
  ),
  stop: (P) => (
    <>
      <polyline points={curve((t) => 0.6 - 0.35 * Math.min(t, 0.6) / 0.6)} fill="none" stroke={P.red} strokeWidth="1.8" />
      <line x1={LEFT} y1={sy(0.25)} x2={RIGHT} y2={sy(0.25)} stroke={P.red} strokeDasharray="4 3" />
      <text x={RIGHT} y={sy(0.25) - 4} fill={P.red} fontSize="8" textAnchor="end">−15¢ → out</text>
      <circle cx={sx(0.6)} cy={sy(0.25)} r="4" fill={P.red} />
      <Axis P={P} />
    </>
  ),
  trail: (P) => (
    <>
      <polyline points={curve((t) => (t < 0.55 ? 0.35 + 0.55 * (t / 0.55) : 0.9 - 0.3 * ((t - 0.55) / 0.45)))} fill="none" stroke={P.green} strokeWidth="1.8" />
      <line x1={LEFT} y1={sy(0.9)} x2={RIGHT} y2={sy(0.9)} stroke={P.muted} strokeDasharray="2 2" />
      <text x={LEFT + 2} y={sy(0.9) - 3} fill={P.muted} fontSize="7.5">best gain</text>
      <line x1={LEFT} y1={sy(0.68)} x2={RIGHT} y2={sy(0.68)} stroke={P.accent} strokeDasharray="4 3" />
      <text x={RIGHT} y={sy(0.68) - 3} fill={P.accent} fontSize="8" textAnchor="end">hands back 40% → sell</text>
      <circle cx={sx(0.88)} cy={sy(0.68)} r="4" fill={P.accent} />
      <Axis P={P} />
    </>
  ),
  holdtest: (P) => (
    <>
      <line x1={LEFT} y1={30} x2={RIGHT} y2={30} stroke={P.border} />
      <text x={RIGHT} y={26} fill={P.faint} fontSize="7.5" textAnchor="end">break even</text>
      <Bar P={P} x={LEFT + 14} w={44} top={30} color={P.red} label="as traded" value="−147" />
      <Bar P={P} x={LEFT + 82} w={44} top={30} color={P.red} label="all held" value="−233" />
      <rect x={LEFT + 150} y={28} width={44} height={4} fill={P.green} rx="2" />
      <text x={LEFT + 172} y={24} fill={P.green} fontSize="8.5" fontWeight="800" textAnchor="middle">0</text>
      <text x={LEFT + 172} y={BASE + 12} fill={P.faint} fontSize="8" textAnchor="middle">not traded</text>
    </>
  ),
  closereason: (P) => (
    <>
      {[["take", P.green, 0.62], ["stop", P.red, 0.3], ["trail", P.accent, 0.75], ["bell", P.muted, 0.5]].map(([lab, col, v], i) => (
        <g key={lab}>
          <rect x={LEFT + i * 54} y={sy(v)} width={40} height={BASE - sy(v)} fill={col} opacity="0.8" rx="2" />
          <text x={LEFT + i * 54 + 20} y={BASE + 12} fill={P.faint} fontSize="7.5" textAnchor="middle">{lab}</text>
        </g>
      ))}
      <text x={sx(0.5)} y={18} fill={P.faint} fontSize="7.5" textAnchor="middle">only “bell” avoids the second fee</text>
      <line x1={LEFT} y1={BASE} x2={RIGHT} y2={BASE} stroke={P.border} />
    </>
  ),

  // ---- the record ----
  pnl: (P) => (
    <>
      <line x1={LEFT} y1={44} x2={RIGHT} y2={44} stroke={P.border} />
      <rect x={LEFT + 10} y={20} width={44} height={24} fill={P.green} opacity="0.8" rx="2" />
      <text x={LEFT + 32} y={16} fill={P.green} fontSize="8" textAnchor="middle">gross</text>
      <rect x={LEFT + 74} y={44} width={44} height={18} fill={P.red} opacity="0.8" rx="2" />
      <text x={LEFT + 96} y={72} fill={P.red} fontSize="8" textAnchor="middle">fees</text>
      <rect x={LEFT + 138} y={44} width={44} height={8} fill={P.red} opacity="0.9" rx="2" />
      <text x={LEFT + 160} y={62} fill={P.red} fontSize="8" fontWeight="800" textAnchor="middle">net</text>
      <text x={RIGHT} y={20} fill={P.faint} fontSize="7.5" textAnchor="end">always shown net</text>
    </>
  ),
  ror: (P) => (
    <>
      <text x={LEFT} y={18} fill={P.faint} fontSize="8">same win rate, opposite outcome</text>
      <Bar P={P} x={LEFT + 20} w={50} top={sy(0.88)} color={P.green} label="won 88%" value="" />
      <Bar P={P} x={LEFT + 120} w={50} top={sy(0.12)} color={P.red} label="lost money" value="" />
      <line x1={LEFT} y1={BASE} x2={RIGHT} y2={BASE} stroke={P.border} />
    </>
  ),
  winrate: (P) => (
    <>
      <circle cx={sx(0.3)} cy={44} r="26" fill="none" stroke={P.border} strokeWidth="7" />
      <circle cx={sx(0.3)} cy={44} r="26" fill="none" stroke={P.green} strokeWidth="7"
        strokeDasharray={`${0.88 * 163} 163`} transform={`rotate(-90 ${sx(0.3)} 44)`} />
      <text x={sx(0.3)} y={48} fill={P.text} fontSize="11" fontWeight="800" textAnchor="middle">88%</text>
      <text x={sx(0.72)} y={40} fill={P.red} fontSize="12" fontWeight="800" textAnchor="middle">−Ⓩ3.10</text>
      <text x={sx(0.72)} y={54} fill={P.faint} fontSize="7.5" textAnchor="middle">never shown alone</text>
    </>
  ),
  brier: (P) => (
    <>
      <text x={LEFT} y={18} fill={P.faint} fontSize="8">said 70% …</text>
      <Bar P={P} x={LEFT + 16} w={48} top={sy(0.09 / 0.5)} color={P.green} label="Up happened" value="0.09" />
      <Bar P={P} x={LEFT + 110} w={48} top={sy(0.49 / 0.5)} color={P.red} label="it did not" value="0.49" />
      <line x1={LEFT} y1={BASE} x2={RIGHT} y2={BASE} stroke={P.border} />
    </>
  ),
  curve: (P) => (
    <>
      <line x1={LEFT} y1={BASE} x2={RIGHT} y2={10} stroke={P.muted} strokeDasharray="3 3" />
      <text x={RIGHT - 30} y={20} fill={P.muted} fontSize="7.5" textAnchor="end">perfect</text>
      {[[0.2, 0.16], [0.4, 0.35], [0.6, 0.64], [0.8, 0.72]].map(([s, came], i) => (
        <circle key={i} cx={sx(s)} cy={sy(came)} r="3.5" fill={Math.abs(came - s) < 0.1 ? P.green : P.accent} />
      ))}
      <text x={LEFT} y={BASE + 12} fill={P.faint} fontSize="8">what it said →</text>
      <text x={RIGHT} y={BASE + 12} fill={P.faint} fontSize="8" textAnchor="end">what came true ↑</text>
    </>
  ),
  holdout: (P) => (
    <>
      <rect x={LEFT} y={30} width={(RIGHT - LEFT) * 0.7} height={22} fill={P.accent} opacity="0.5" rx="2" />
      <text x={LEFT + (RIGHT - LEFT) * 0.35} y={45} fill={P.text} fontSize="8.5" textAnchor="middle">trained on these</text>
      <rect x={LEFT + (RIGHT - LEFT) * 0.7} y={30} width={(RIGHT - LEFT) * 0.3} height={22} fill={P.green} opacity="0.6" rx="2" />
      <text x={LEFT + (RIGHT - LEFT) * 0.85} y={45} fill={P.text} fontSize="8" textAnchor="middle">tested here</text>
      {/* one line of this ran past both edges of the frame; two lines fit */}
      <text x={sx(0.5)} y={16} fill={P.faint} fontSize="7" textAnchor="middle">it must beat the simple correction</text>
      <text x={sx(0.5)} y={25} fill={P.faint} fontSize="7" textAnchor="middle">on windows it never saw</text>
      <Axis P={P} left="oldest" right="newest" />
    </>
  ),
  judgement: (P) => (
    <>
      <rect x={LEFT} y={36} width={RIGHT - LEFT} height={14} fill={P.border} opacity="0.5" rx="7" />
      <rect x={LEFT} y={36} width={(RIGHT - LEFT) * 0.21} height={14} fill={P.accent} rx="7" />
      <text x={LEFT} y={30} fill={P.accent} fontSize="8.5" fontWeight="800">42 windows</text>
      <text x={RIGHT} y={30} fill={P.faint} fontSize="8" textAnchor="end">200 → judged</text>
      <text x={sx(0.5)} y={66} fill={P.faint} fontSize="7.5" textAnchor="middle">windows, never wagers</text>
    </>
  ),
  windows: (P) => (
    <>
      {[...Array(6)].map((_, i) => (
        <circle key={i} cx={LEFT + 14 + i * 16} cy={30} r="5.5" fill={P.accent} opacity="0.75" />
      ))}
      <text x={LEFT + 120} y={34} fill={P.faint} fontSize="8">6 wagers…</text>
      <path d={`M ${LEFT + 46} 40 L ${LEFT + 46} 52`} stroke={P.muted} strokeWidth="1.2" markerEnd="" />
      <circle cx={LEFT + 46} cy={60} r="7" fill={P.green} />
      <text x={LEFT + 62} y={64} fill={P.green} fontSize="8.5" fontWeight="800">…but one outcome</text>
    </>
  ),
  eras: (P) => (
    <>
      {[["first night", P.faint, 0.22], ["tilt", P.red, 0.36], ["strategy", P.green, 0.42]].map(([lab, col, w], i, arr) => {
        const x0 = LEFT + arr.slice(0, i).reduce((s, a) => s + a[2], 0) * (RIGHT - LEFT);
        return (
          <g key={lab}>
            <rect x={x0} y={32} width={w * (RIGHT - LEFT) - 3} height={20} fill={col} opacity="0.45" rx="2" />
            <text x={x0 + (w * (RIGHT - LEFT)) / 2} y={46} fill={P.text} fontSize="7.5" textAnchor="middle">{lab}</text>
          </g>
        );
      })}
      <text x={sx(0.5)} y={22} fill={P.faint} fontSize="7.5" textAnchor="middle">never merged into one number</text>
      <Axis P={P} />
    </>
  ),
  projection: (P) => (
    <>
      <line x1={LEFT} y1={44} x2={RIGHT} y2={44} stroke={P.border} strokeWidth="1.2" />
      <text x={LEFT} y={40} fill={P.faint} fontSize="7.5">0</text>
      <line x1={sx(0.12)} y1={44} x2={sx(0.86)} y2={44} stroke={P.accent} strokeWidth="6" opacity="0.4" />
      <line x1={sx(0.12)} y1={34} x2={sx(0.12)} y2={54} stroke={P.red} strokeWidth="2" />
      <line x1={sx(0.86)} y1={34} x2={sx(0.86)} y2={54} stroke={P.green} strokeWidth="2" />
      <circle cx={sx(0.38)} cy={44} r="4.5" fill={P.text} />
      <text x={sx(0.38)} y={30} fill={P.text} fontSize="8" textAnchor="middle">estimate</text>
      <text x={sx(0.5)} y={66} fill={P.red} fontSize="8" fontWeight="700" textAnchor="middle">the range crosses zero</text>
    </>
  ),

  // ---- money ----
  stake: (P) => (
    <>
      {[...Array(6)].map((_, i) => (
        <rect key={i} x={LEFT + 6 + i * 36} y={34} width={28} height={20} fill={P.accent} opacity="0.7" rx="3" />
      ))}
      <text x={sx(0.5)} y={24} fill={P.faint} fontSize="8" textAnchor="middle">Ⓩ5 a rule · up to Ⓩ30 at risk at once</text>
      <Axis P={P} />
    </>
  ),
  bank: (P) => (
    <>
      <rect x={LEFT} y={30} width={RIGHT - LEFT} height={24} fill={P.border} opacity="0.4" rx="3" />
      <rect x={LEFT} y={30} width={(RIGHT - LEFT) * 0.72} height={24} fill={P.green} opacity="0.65" rx="3" />
      <text x={LEFT + 8} y={46} fill={P.text} fontSize="9" fontWeight="700">Ⓩ360</text>
      <text x={RIGHT - 4} y={46} fill={P.faint} fontSize="8" textAnchor="end">of Ⓩ500</text>
      <text x={sx(0.5)} y={22} fill={P.faint} fontSize="7.5" textAnchor="middle">its own money — not ZBucks</text>
      <Axis P={P} />
    </>
  ),
  graduation: (P) => (
    <>
      <rect x={LEFT} y={28} width={70} height={28} fill={P.border} opacity="0.5" rx="3" />
      <text x={LEFT + 35} y={46} fill={P.text} fontSize="8" textAnchor="middle">desk bank</text>
      <rect x={LEFT + 92} y={24} width={16} height={36} fill={P.green} opacity="0.35" rx="2" stroke={P.green} />
      <text x={LEFT + 100} y={18} fill={P.green} fontSize="7" textAnchor="middle">200</text>
      <path d={`M ${LEFT + 74} 42 L ${LEFT + 88} 42`} stroke={P.muted} strokeWidth="1.5" />
      <path d={`M ${LEFT + 112} 42 L ${LEFT + 128} 42`} stroke={P.green} strokeWidth="1.5" />
      <rect x={LEFT + 132} y={28} width={74} height={28} fill={P.green} opacity="0.25" rx="3" stroke={P.green} />
      <text x={LEFT + 169} y={46} fill={P.green} fontSize="8" textAnchor="middle">ZBucks</text>
      <Axis P={P} label="the only door, and it opens on return on risk" />
    </>
  ),
  currency: (P) => (
    <>
      <text x={sx(0.32)} y={50} fill={P.text} fontSize="20" fontWeight="800" textAnchor="middle">Ⓩ</text>
      <text x={sx(0.5)} y={48} fill={P.faint} fontSize="14" textAnchor="middle">⇄</text>
      <text x={sx(0.68)} y={50} fill={P.text} fontSize="20" fontWeight="800" textAnchor="middle">$</text>
      <text x={sx(0.5)} y={68} fill={P.faint} fontSize="7.5" textAnchor="middle">a display change only — cents stay cents</text>
    </>
  ),
};

// Group fallbacks, so an entry without bespoke art still gets a picture.
const GROUP_ART = {
  window: ART.target, market: ART.price, agents: ART.raw,
  decision: ART.decision, exits: ART.mark, record: ART.judgement, money: ART.bank,
};

export function hasArt(id) {
  return Boolean(ART[id]);
}

/** The diagram for an entry, as an <svg>. Never throws: a broken picture
 *  must not take down the explanation it sits above. */
export default function GlossaryArt({ entry, palette, ctx }) {
  if (!entry) return null;
  const P = palette || {};
  const draw = ART[entry.id] || GROUP_ART[entry.group];
  if (!draw) return null;
  let body = null;
  try {
    body = draw(P, ctx || {});
  } catch {
    return null;
  }
  return (
    <svg
      data-testid={`btc15-glossary-art-${entry.id}`}
      viewBox={`0 0 ${W} ${H + 6}`}
      role="img"
      aria-label={`Diagram: ${entry.term}`}
      style={{
        display: "block", width: "100%", height: "auto", maxWidth: 340, margin: "2px 0 10px",
        background: P.surface, border: `1px solid ${P.border}`, borderRadius: 8,
      }}
    >
      <title>{`Diagram: ${entry.term}`}</title>
      {body}
    </svg>
  );
}
